import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Home,
  AdminPanelSettings,
  Chat,
  Science,
  ExpandMore,
  ExpandLess,
  Add,
  Mic,
  Inventory2Outlined as Archive,
  InfoOutlined,
  LocalActivityOutlined,
  Videocam,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import ReplayModal from "../Replay/ReplayModal";
import { getChatSession } from "../../lib/api/Chat";
import { GetSessionRes } from "../../lib/models/Chat";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import "./ContentPanel.css";
import { config } from "@config";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { useChatSessionsContext } from "../../lib/contexts/ChatSessionsContext";
import { Persona } from "../../lib/api/Persona";
import { usePersona } from "../../lib/hooks/UsePersona";
import { isAnyAdmin } from "@common/domain/auth";
import { stripExtraTags } from "@common/domain/tag-filter";
import AccountMenu from "../AccountMenu/AccountMenu";
import { AdminSidebarItems } from "../AdminSidebarItems";

interface ContentPanelProps {
  onItemClick?: () => void;
}

const ContentPanel = ({ onItemClick }: ContentPanelProps) => {
  const { userInfo } = useUserInfoContext();
  const {
    chatSessions,
    selectedChatSession,
    updateSelectedChatSession,
    resetSelectedChatSession,
    hideChatSession,
    persona: selectedPersona,
    updatePersona,
  } = useChatSessionsContext();
  const { getPersonas } = usePersona();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [chatOpen, setChatOpen] = useState(true);
  const [allPersonas, setAllPersonas] = useState<Persona[]>([]);

  // Replay modal state from chat history
  const [replayModalOpen, setReplayModalOpen] = useState(false);
  const [replaySession, setReplaySession] = useState<GetSessionRes | null>(null);
  const [replayTargetSession, setReplayTargetSession] = useState<any>(null);
  const [replayLoadingId, setReplayLoadingId] = useState<string | null>(null);
  const [replayError, setReplayError] = useState<string | null>(null);

  const handleStartReplayFromHistory = async (session: any) => {
    if (!userInfo) return;
    setReplayTargetSession(session);
    setReplayLoadingId(session.chatInfo.chatId);
    setReplayError(null);

    try {
      const fullSession = await getChatSession(userInfo, session.chatInfo.chatId);
      if (fullSession && fullSession.conversation) {
        setReplaySession(fullSession);
        setReplayModalOpen(true);
      } else {
        setReplayError("Unable to load chat transcript for replay.");
      }
    } catch (err: any) {
      console.error("Failed to load chat session for replay", err);
      setReplayError(`Error loading conversation for replay: ${err?.message || 'Unknown error'}`);
    } finally {
      setReplayLoadingId(null);
    }
  };

  useEffect(() => {
    if (userInfo) {
      getPersonas().then(setAllPersonas).catch(err => console.error("Failed to fetch personas", err));
    }
  }, [userInfo, getPersonas]);

  const sessionsGroupedByMonths = useMemo(() => {
    const filtered = chatSessions.filter(
      session => !selectedPersona || session.chatInfo.personaId === selectedPersona.id
    );
    const grouped = filtered.reduce<Record<string, typeof filtered>>((acc, session) => {
      const month = session.month;
      if (!acc[month]) acc[month] = [];
      acc[month].push(session);
      return acc;
    }, {});
    Object.keys(grouped).forEach(month => {
      grouped[month].sort((a, b) => {
        const dateA = a.chatInfo.timestamp ? new Date(a.chatInfo.timestamp).getTime() : 0;
        const dateB = b.chatInfo.timestamp ? new Date(b.chatInfo.timestamp).getTime() : 0;
        return dateB - dateA;
      });
    });
    return grouped;
  }, [chatSessions, selectedPersona]);

  const sortedMonthNames = useMemo(() => {
    return Object.keys(sessionsGroupedByMonths).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }, [sessionsGroupedByMonths]);

  return (
    <Box className="content-panel">
      {/* Header */}
      <Box className="content-panel-header">
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 40,
            height: 40,
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          {config.shortName || "DC"}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {config.appName || "Digital Companion"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Workspace
          </Typography>
        </Box>
      </Box>

      {/* Main Navigation Section */}
      <Box className="content-panel-section">
        <List className="content-panel-nav" disablePadding>
          {!config.showHomeTab ? (
            <>
              {/* Conversation (Points to /text-chat for Lincoln) */}
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/text-chat"
                  className="nav-item"
                  onClick={onItemClick}
                >
                  <ListItemIcon>
                    <Chat />
                  </ListItemIcon>
                  <ListItemText primary="Conversation" />
                </ListItemButton>
                <Box
                  className="nav-item-expand"
                  onClick={(e) => {
                    e.stopPropagation();
                    setChatOpen(!chatOpen);
                  }}
                >
                  {chatOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/"
                  end
                  className="nav-item"
                  onClick={() => {
                    resetSelectedChatSession();
                    updatePersona(undefined);
                    onItemClick?.();
                  }}
                >
                  <ListItemIcon>
                    <Home />
                  </ListItemIcon>
                  <ListItemText primary="Home" />
                </ListItemButton>
              </ListItem>

              {/* Chat with collapsible history */}
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/text-chat"
                  className="nav-item"
                  onClick={onItemClick}
                >
                  <ListItemIcon>
                    <Chat />
                  </ListItemIcon>
                  <ListItemText primary="Chat" />
                </ListItemButton>
                <Box
                  className="nav-item-expand"
                  onClick={(e) => {
                    e.stopPropagation();
                    setChatOpen(!chatOpen);
                  }}
                >
                  {chatOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </ListItem>
            </>
          )}

          <Collapse in={chatOpen} timeout="auto" unmountOnExit>
            <Box className="chat-history-section">
              <ListItemButton
                className="new-chat-item"
                onClick={() => {
                  resetSelectedChatSession();
                  if (pathname !== "/text-chat") {
                    navigate("/text-chat");
                  }
                  onItemClick?.();
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Add fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="New Conversation"
                  primaryTypographyProps={{ fontSize: 'var(--text-sm)' }}
                />
              </ListItemButton>

              <Box className="chat-history-list">
                {sortedMonthNames.length === 0 ? (
                  <Typography className="chat-history-empty">
                    No previous chats
                  </Typography>
                ) : (
                  sortedMonthNames.map(month => (
                    <Box key={month} className="chat-history-group">
                      <Typography className="chat-history-month">
                        {month}
                      </Typography>
                      {sessionsGroupedByMonths[month].map(session => (
                        <ListItemButton
                          key={session.chatInfo.chatId}
                          className={`chat-history-item ${selectedChatSession === session.chatInfo.chatId ? 'active' : ''}`}
                          onClick={() => {
                            const sessionPersona = allPersonas.find(p => p.id === session.chatInfo.personaId);
                            if (sessionPersona && selectedPersona?.id !== sessionPersona.id) {
                              updatePersona(sessionPersona);
                            }
                            updateSelectedChatSession(session.chatInfo.chatId);
                            navigate(`/text-chat/${session.chatInfo.chatId}`);
                            onItemClick?.();
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  minWidth: 0,
                                  width: "100%",
                                }}
                              >
                                {session.chatInfo.isConversation && (
                                  <Tooltip title="Video Call" placement="top">
                                    <Videocam
                                      fontSize="inherit"
                                      sx={{
                                        fontSize: "0.95rem",
                                        color: "primary.main",
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                <Box
                                  component="span"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    minWidth: 0,
                                  }}
                                >
                                  {stripExtraTags(session.chatInfo.chatTitle)}
                                </Box>
                              </Box>
                            }
                            primaryTypographyProps={{
                              fontSize: 'var(--text-sm)',
                              noWrap: true,
                            }}
                          />
                          {isAnyAdmin(userInfo?.groups) && (
                            <IconButton
                              size="small"
                              className="chat-replay-btn"
                              title="Replay conversation"
                              disabled={replayLoadingId === session.chatInfo.chatId}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartReplayFromHistory(session);
                              }}
                              sx={{ p: 0.25, ml: 'auto' }}
                            >
                              <ReplayIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            className="chat-delete-btn"
                            title="Archive conversation"
                            onClick={(e) => {
                              e.stopPropagation();
                              hideChatSession(session.chatInfo.chatId);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Archive sx={{ fontSize: 14 }} />
                          </IconButton>
                        </ListItemButton>
                      ))}
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Collapse>

          {/* Custom Brand Tabs */}
          {(config.customTabs || []).map((tab: any) => (
            <ListItem disablePadding key={tab.path}>
              <ListItemButton
                component={NavLink}
                to={tab.path}
                className="nav-item"
                onClick={onItemClick}
              >
                <ListItemIcon>
                  {tab.icon === "about" ? <InfoOutlined /> : <LocalActivityOutlined />}
                </ListItemIcon>
                <ListItemText primary={tab.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Admin Section - only for admins */}
      {Boolean(config.learningPersonaId) && isAnyAdmin(userInfo?.groups) && (
        <>
          <Divider className="content-panel-divider" />
          <Box className="content-panel-section">
            <Typography className="section-label">Admin</Typography>
            <List className="content-panel-nav" disablePadding>
              <AdminSidebarItems
                itemClassName="nav-item"
                onItemClick={onItemClick}
                showConversationResults={Boolean(config.showConversationResults)}
              />
            </List>
          </Box>
        </>
      )}

      {!Boolean(config.learningPersonaId) && config.showAdminTabs && isAnyAdmin(userInfo?.groups) && (
        <>
          <Divider className="content-panel-divider" />
          <Box className="content-panel-section">
            <Typography className="section-label">Admin</Typography>
            <List className="content-panel-nav" disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/admin/eval-sim"
                  className="nav-item"
                  onClick={onItemClick}
                >
                  <ListItemIcon>
                    <Science />
                  </ListItemIcon>
                  <ListItemText primary="Evaluation Simulator" />
                </ListItemButton>
              </ListItem>
              <AdminListItem onItemClick={onItemClick} />
              <ListItem disablePadding>
                <ListItemButton
                  component={NavLink}
                  to="/admin/audio-notes"
                  className="nav-item"
                  onClick={onItemClick}
                >
                  <ListItemIcon>
                    <Mic />
                  </ListItemIcon>
                  <ListItemText primary="Audio Notes" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </>
      )}

      {/* Interactive Replay Modal */}
      {replayModalOpen && replayTargetSession && replaySession && (
        <ReplayModal
          open={replayModalOpen}
          onClose={() => setReplayModalOpen(false)}
          originalChatId={replayTargetSession.chatInfo.chatId}
          originalUserId={userInfo?.userId}
          originalUserEmail={userInfo?.email}
          originalPersonaId={replayTargetSession.chatInfo.personaId}
          originalTitle={replayTargetSession.chatInfo.chatTitle}
          originalConversation={replaySession.conversation || []}
          personas={allPersonas}
        />
      )}

      {/* Replay Error Snackbar */}
      <Snackbar
        open={Boolean(replayError)}
        autoHideDuration={4000}
        onClose={() => setReplayError(null)}
      >
        <Alert severity="error" onClose={() => setReplayError(null)}>
          {replayError}
        </Alert>
      </Snackbar>

      {/* Spacer to push profile to bottom */}
      <Box sx={{ flex: 1 }} />

      {/* Profile Section at Bottom */}
      <Divider className="content-panel-divider" />
      <AccountMenu onItemClick={onItemClick} />
    </Box>
  );
};

const AdminListItem = ({ onItemClick }: { onItemClick?: () => void }) => {
  const { pathname } = useLocation();
  const isActive =
    pathname.startsWith("/admin") &&
    !pathname.startsWith("/admin/eval-sim") &&
    !pathname.startsWith("/admin/audio-notes");

  return (
    <ListItem disablePadding>
      <ListItemButton
        component={Link}
        to="/admin"
        className={`nav-item ${isActive ? "active" : ""}`}
        onClick={onItemClick}
      >
        <ListItemIcon>
          <AdminPanelSettings />
        </ListItemIcon>
        <ListItemText primary="Admin" />
      </ListItemButton>
    </ListItem>
  );
};

export default ContentPanel;
